import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { ArrowLeft, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';

type DocumentFieldType = 'text' | 'textarea' | 'number' | 'date';

type DocumentTypeField = {
  id: string;
  label: string;
  type: DocumentFieldType;
  required: boolean;
};

type DocumentTypeRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  fields: DocumentTypeField[];
  createdAt: string;
  updatedAt: string;
};

type EditableDocumentType = {
  id?: string;
  code: string;
  name: string;
  description: string;
  isActive: boolean;
  fields: DocumentTypeField[];
};

const fieldTypeOptions: Array<{ value: DocumentFieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

const documentTypesItOverrides: Record<string, string> = {
  'Document Types': 'Tipi di documento',
  'Manage the document categories and metadata required during uploads.':
    'Gestisci le categorie documentali e i metadati richiesti durante i caricamenti.',
  'Loading document type...': 'Caricamento tipo documento...',
  'Configure the document type and the metadata fields required during uploads.':
    'Configura il tipo di documento e i campi metadato richiesti durante i caricamenti.',
  Settings: 'Impostazioni',
  'Add Type': 'Aggiungi tipo',
  Catalog: 'Catalogo',
  'Configured Types': 'Tipi configurati',
  Loading: 'Caricamento...',
  total: 'totale',
  Name: 'Nome',
  Code: 'Codice',
  Description: 'Descrizione',
  Fields: 'Campi',
  Status: 'Stato',
  Updated: 'Aggiornato',
  Actions: 'Azioni',
  Active: 'Attivo',
  Inactive: 'Inattivo',
  Edit: 'Modifica',
  'No document types yet.': 'Nessun tipo di documento presente.',
  'Document type not found.': 'Tipo di documento non trovato.',
  'Unable to load document types.': 'Impossibile caricare i tipi di documento.',
  'Name is required.': 'Il nome è obbligatorio.',
  'Code is required.': 'Il codice è obbligatorio.',
  'Each document field needs an id and label.': 'Ogni campo documento richiede un ID e un’etichetta.',
  'Document type updated.': 'Tipo di documento aggiornato.',
  'Document type created.': 'Tipo di documento creato.',
  'Failed to save document type.': 'Impossibile salvare il tipo di documento.',
  'Failed to delete document type.': 'Impossibile eliminare il tipo di documento.',
  Delete: 'Elimina',
  'Saving...': 'Salvataggio...',
  'Save Changes': 'Salva modifiche',
  'Active for uploads': 'Attivo per i caricamenti',
  'Optional context for clinicians during upload.': 'Contesto facoltativo per i clinici durante il caricamento.',
  'Required Upload Fields': 'Campi obbligatori al caricamento',
  'Title is always required. Description is always optional. Add any extra fields for this type here.':
    'Il titolo è sempre obbligatorio. La descrizione è sempre facoltativa. Aggiungi qui eventuali campi extra per questo tipo.',
  'Add Field': 'Aggiungi campo',
  'No type-specific fields yet.': 'Ancora nessun campo specifico per questo tipo.',
  'Field ID': 'ID campo',
  Label: 'Etichetta',
  Type: 'Tipo',
  Required: 'Obbligatorio',
  'Remove field': 'Rimuovi campo',
  'New Document Type': 'Nuovo tipo di documento',
  Back: 'Indietro',
};

function createDocumentTypesTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = documentTypesItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

function createField(): DocumentTypeField {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return {
    id: `field_${id}`,
    label: '',
    type: 'text',
    required: true,
  };
}

function createDraft(): EditableDocumentType {
  return {
    code: '',
    name: '',
    description: '',
    isActive: true,
    fields: [],
  };
}

function toDraft(row: DocumentTypeRow): EditableDocumentType {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    description: row.description || '',
    isActive: row.isActive,
    fields: row.fields || [],
  };
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function DocumentTypesListPage({
  rows,
  loading,
  error,
  onBack,
  onAdd,
  onOpen,
}: {
  rows: DocumentTypeRow[];
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onAdd: () => void;
  onOpen: (id: string) => void;
}) {
  const { language, tr } = useI18n();
  const td = createDocumentTypesTranslator(language, tr);
  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="rounded-full p-2 hover:bg-slate-100" aria-label={td('Back')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">{td('Settings')}</p>
            <h1 className="text-2xl font-semibold text-ink">{td('Document Types')}</h1>
            <p className="text-sm text-slate-500">{td('Manage the document categories and metadata required during uploads.')}</p>
          </div>
        </div>
        <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-soft">
          <Plus size={16} />
          {td('Add Type')}
        </button>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{td('Catalog')}</p>
            <h2 className="font-semibold text-ink">{td('Configured Types')}</h2>
          </div>
          <div className="text-sm text-slate-500">{loading ? td('Loading') : `${rows.length} ${td('total')}`}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-4 font-medium">{td('Name')}</th>
                <th className="p-4 font-medium">{td('Code')}</th>
                <th className="p-4 font-medium">{td('Description')}</th>
                <th className="p-4 font-medium">{td('Fields')}</th>
                <th className="p-4 font-medium">{td('Status')}</th>
                <th className="p-4 font-medium">{td('Updated')}</th>
                <th className="p-4 text-right font-medium">{td('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="p-4 font-medium text-ink">{row.name}</td>
                  <td className="p-4 text-slate-600">{row.code}</td>
                  <td className="max-w-[360px] p-4 text-slate-600">{row.description || '—'}</td>
                  <td className="p-4 text-slate-600">{row.fields.length}</td>
                  <td className="p-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        row.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {row.isActive ? td('Active') : td('Inactive')}
                    </span>
                  </td>
                  <td className="p-4 text-slate-600">{formatDate(row.updatedAt)}</td>
                  <td className="p-4 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(row.id)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
                    >
                      <Pencil size={14} />
                      {td('Edit')}
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && !loading ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-slate-500">
                    {td('No document types yet.')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DocumentTypeDetailPage({
  mode,
  rows,
  onReload,
}: {
  mode: 'new' | 'edit';
  rows: DocumentTypeRow[];
  onReload: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const { language, tr } = useI18n();
  const td = createDocumentTypesTranslator(language, tr);
  const { documentTypeId } = useParams();
  const { fetchJson, fetchApi } = useApiClient();
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<EditableDocumentType>(createDraft());
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedRow = useMemo(
    () => (mode === 'edit' ? rows.find((row) => row.id === documentTypeId) ?? null : null),
    [documentTypeId, mode, rows],
  );

  const loadDetail = useCallback(async () => {
    if (mode === 'new') {
      setDraft(createDraft());
      setLoading(false);
      return;
    }
    if (!documentTypeId) {
      setError(td('Document type not found.'));
      setLoading(false);
      return;
    }

    const existing = rows.find((row) => row.id === documentTypeId);
    if (existing) {
      setDraft(toDraft(existing));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<DocumentTypeRow[]>('/document-types');
      const row = data.find((entry) => entry.id === documentTypeId);
      if (!row) {
        setError(td('Document type not found.'));
        return;
      }
      setDraft(toDraft(row));
    } catch (err: any) {
      setError(err.message || td('Unable to load document types.'));
    } finally {
      setLoading(false);
    }
  }, [documentTypeId, fetchJson, mode, rows]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  async function saveDocumentType(event: FormEvent) {
    event.preventDefault();

    if (!draft.name.trim()) {
      setError(td('Name is required.'));
      return;
    }
    if (!draft.code.trim()) {
      setError(td('Code is required.'));
      return;
    }
    if (draft.fields.some((field) => !field.label.trim() || !field.id.trim())) {
      setError(td('Each document field needs an id and label.'));
      return;
    }

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const body = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        isActive: draft.isActive,
        fields: draft.fields.map((field) => ({
          id: field.id.trim(),
          label: field.label.trim(),
          type: field.type,
          required: field.required,
        })),
      };
      const isEdit = Boolean(draft.id);
      const path = isEdit ? `/document-types/${draft.id}` : '/document-types';
      const method = isEdit ? 'PUT' : 'POST';
      const saved = await fetchJson<DocumentTypeRow>(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await onReload();
      setDraft(toDraft(saved));
      setBanner(isEdit ? td('Document type updated.') : td('Document type created.'));
      if (!isEdit) {
        navigate(`/settings/document-types/${saved.id}`, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || td('Failed to save document type.'));
    } finally {
      setSaving(false);
    }
  }

  async function deleteDocumentType() {
    if (!draft.id) {
      navigate('/settings/document-types');
      return;
    }
    if (!window.confirm(`Delete "${draft.name}"?`)) return;

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const res = await fetchApi(`/document-types/${draft.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.message || `Delete failed (${res.status})`);
      }
      await onReload();
      navigate('/settings/document-types');
    } catch (err: any) {
      setError(err.message || td('Failed to delete document type.'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings/document-types')} className="rounded-full p-2 hover:bg-slate-100" aria-label={td('Back')}>
            <ArrowLeft size={20} />
          </button>
          <div className="text-sm text-slate-500">{td('Loading document type...')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings/document-types')} className="rounded-full p-2 hover:bg-slate-100" aria-label={td('Back')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">{td('Settings')}</p>
            <h1 className="text-2xl font-semibold text-ink">{mode === 'new' ? td('New Document Type') : selectedRow?.name || td('Document Types')}</h1>
            <p className="text-sm text-slate-500">{td('Configure the document type and the metadata fields required during uploads.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'edit' ? (
            <button
              type="button"
              onClick={deleteDocumentType}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 hover:bg-rose-100 disabled:opacity-60"
            >
              <Trash2 size={16} />
              {td('Delete')}
            </button>
          ) : null}
          <button
            type="submit"
            form="document-type-form"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-medium text-white shadow-soft disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? td('Saving...') : td('Save Changes')}
          </button>
        </div>
      </div>

      {banner ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{banner}</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <form id="document-type-form" onSubmit={saveDocumentType} className="space-y-6">
        <div className="card p-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium text-slate-500">{td('Name')}</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                placeholder="Lab Report"
              />
            </label>
            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium text-slate-500">{td('Code')}</span>
              <input
                value={draft.code}
                onChange={(event) => setDraft((prev) => ({ ...prev, code: event.target.value }))}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                placeholder="lab_report"
              />
            </label>
            <label className="flex items-center gap-3 pt-7 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => setDraft((prev) => ({ ...prev, isActive: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span>{td('Active for uploads')}</span>
            </label>
          </div>

          <label className="block space-y-1 text-sm text-slate-700">
            <span className="font-medium text-slate-500">{td('Description')}</span>
            <textarea
              value={draft.description}
              onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
              className="min-h-[96px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
              placeholder={td('Optional context for clinicians during upload.')}
            />
          </label>
        </div>

        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">{td('Required Upload Fields')}</h2>
              <p className="text-sm text-slate-500">
                {td('Title is always required. Description is always optional. Add any extra fields for this type here.')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDraft((prev) => ({ ...prev, fields: [...prev.fields, createField()] }))}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Plus size={16} />
              {td('Add Field')}
            </button>
          </div>

          {!draft.fields.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">{td('No type-specific fields yet.')}</div>
          ) : null}

          {draft.fields.map((field, index) => (
            <div key={field.id} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-[1.1fr_1.2fr_180px_auto]">
              <label className="space-y-1 text-sm text-slate-700">
                <span className="font-medium text-slate-500">{td('Field ID')}</span>
                <input
                  value={field.id}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      fields: prev.fields.map((entry, entryIndex) => (entryIndex === index ? { ...entry, id: event.target.value } : entry)),
                    }))
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                  placeholder="lab_date"
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="font-medium text-slate-500">{td('Label')}</span>
                <input
                  value={field.label}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      fields: prev.fields.map((entry, entryIndex) => (entryIndex === index ? { ...entry, label: event.target.value } : entry)),
                    }))
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                  placeholder="Lab Date"
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="font-medium text-slate-500">{td('Type')}</span>
                <select
                  value={field.type}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      fields: prev.fields.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, type: event.target.value as DocumentFieldType } : entry,
                      ),
                    }))
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                >
                  {fieldTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        fields: prev.fields.map((entry, entryIndex) =>
                          entryIndex === index ? { ...entry, required: event.target.checked } : entry,
                        ),
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                  />
                  <span>{td('Required')}</span>
                </label>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, fields: prev.fields.filter((_, entryIndex) => entryIndex !== index) }))}
                  className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"
                  title={td('Remove field')}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </form>
    </div>
  );
}

export default function SettingsDocumentTypes() {
  const navigate = useNavigate();
  const isNewRoute = Boolean(useMatch('/settings/document-types/new'));
  const { documentTypeId } = useParams();
  const { fetchJson } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DocumentTypeRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<DocumentTypeRow[]>('/document-types');
      setRows(data);
    } catch (err: any) {
      setError(err.message || 'Unable to load document types.');
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  if (isNewRoute) {
    return <DocumentTypeDetailPage mode="new" rows={rows} onReload={loadRows} />;
  }

  if (documentTypeId) {
    return <DocumentTypeDetailPage mode="edit" rows={rows} onReload={loadRows} />;
  }

  return (
    <DocumentTypesListPage
      rows={rows}
      loading={loading}
      error={error}
      onBack={() => navigate('/settings')}
      onAdd={() => navigate('/settings/document-types/new')}
      onOpen={(id) => navigate(`/settings/document-types/${id}`)}
    />
  );
}
